import React, { useMemo } from "react";
import { useQuery } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import * as PR from "../../prime-modules/index";
import Layout from "../../components/layout/Layout";
import Loading from "../../components/Loading";
import { useToast } from "../../context/ToastContext";
import { getKycStatusApi } from "../../services/api";
import { updateUserData } from "../../store/auth.slice";
import "./Kyc.scss";
import { temporaryTranslations } from "../../assets/data/temporaryTranslations";

const KYC_PENDING_STATUS = "pending";
const KYC_PASS_STATUS = "pass";
const KYC_FAIL_STATUS = "fail";

const getStatusValue = (result) =>
  result?.data?.status ?? result?.status ?? "";

const KycDetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { userData } = useSelector((state) => state.auth);
  const { myAccount: account } = useSelector(
    (state) => state.language?.activelang,
  );

  const copy = useMemo(
    () => ({
      completeKyc: account?.ACTN0139,
      title: account?.ACTN0140,
      completed: account?.ACTN0143,
      pending: account?.ACTN0144,
      completedDescription: account?.ACTN0145,
      pendingDescription: account?.ACTN0146,
      refreshError: account?.ACTN0131,
      beforeStart: account?.ACTN0133,
      beforeStartDescription: account?.ACTN0134,
      beforeStartChecklist: account?.ACTN0135,
      statusUpdated: account?.ACTN0136,
      currentStatus: account?.ACTN0137,
      reviewComplete: account?.ACTN0138,
    }),
    [account],
  );

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["kyc-status"],
    queryFn: getKycStatusApi,
    refetchOnWindowFocus: false,
    onSuccess: (result) => {
      const currentStatus = getStatusValue(result);
      if (currentStatus === KYC_PASS_STATUS) {
        dispatch(updateUserData({ ...userData }));
      }
    },
    onError: (error) => {
      showToast("error", error?.message || copy.refreshError);
    },
  });

  const apiKycStatus = getStatusValue(data);
  const hasKycDecision =
    apiKycStatus !== "" && apiKycStatus !== KYC_PENDING_STATUS;
  const isKycCompleted =
    apiKycStatus === KYC_PASS_STATUS;
  const isKycInReview = apiKycStatus === KYC_PENDING_STATUS;
  const isKycNotStarted =
    apiKycStatus === "" && !isKycInReview && !isKycCompleted;
  const hasKycFinalResult = hasKycDecision && !isKycCompleted;
  const resultIcon =
    apiKycStatus === KYC_FAIL_STATUS
      ? "pi pi-times-circle"
      : "pi pi-exclamation-circle";
  const resultTone =
    apiKycStatus === KYC_FAIL_STATUS ? "kyc-danger" : "kyc-warning";

  const renderVerificationButton = () => (
    <PR.Button
      label={copy.completeKyc}
      className="signup-button kyc-primary-button"
      onClick={() => navigate("/kyc-verification")}
    />
  );

  return (
    <Layout>
      <section className="kyc-section">
        <div className="kyc-header">
          <div className="col-12 text-center">
            <h1>{account?.ACTN0140}</h1>
            <p>{account.ACTN0150 ||temporaryTranslations.ACTN0150}</p>
          </div>
        </div>
        {isLoading ? (
          <Loading />
        ) : (
          <div className="kyc-card-stack">
            <div className="kyc-refresh-container">
              <PR.Button
                icon={isFetching ? "pi pi-spin pi-spinner" : "pi pi-refresh"}
                className="p-button-text kyc-refresh-button"
                onClick={() => refetch()}
                disabled={isFetching}
                aria-label={copy.title}
                title={account.ACTN0149 ||temporaryTranslations.ACTN0149}
              />
            </div>
            {isKycInReview ? (
              <div className="kyc-status-card">
                <i className="pi pi-clock kyc-icon kyc-warning"></i>
                <h2>{copy.pending}</h2>
                <p>{copy.pendingDescription}</p>
              </div>
            ) : null}

            {isKycNotStarted ? (
              <div className="kyc-status-card kyc-info-card">
                <h2>{copy.beforeStart}</h2>
                <p>{copy.beforeStartDescription}</p>
                <ol className="kyc-checklist">
                  {copy.beforeStartChecklist
                    .split(",")
                    .map((item) => item.trim())
                    .filter(Boolean)
                    .map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                </ol>
                {renderVerificationButton()}
              </div>
            ) : null}

            {hasKycFinalResult ? (
              <div className="kyc-status-card">
                <i className={`${resultIcon} kyc-icon ${resultTone}`}></i>
                <h2>{copy.statusUpdated}</h2>
                <p className="kyc-status-pill">
                  {copy.currentStatus} {apiKycStatus.toUpperCase()}
                </p>
                <p>{copy.reviewComplete}</p>
              </div>
            ) : null}

            {isKycCompleted ? (
              <div className="kyc-status-card">
                <i className="pi pi-check-circle kyc-icon kyc-success"></i>
                <h2>{copy.completed}</h2>
                <p>{copy.completedDescription}</p>
              </div>
            ) : null}
          </div>
        )}
      </section>
    </Layout>
  );
};

export default KycDetails;
